/* about-stats.js — count the proof figures up when the row is first seen.
 *
 * Four large numbers that never move is the most static moment on the page, so
 * they tween from zero on first intersection. Everything here is an enhancement
 * on top of a finished render: the Liquid already prints the real value, and the
 * last thing this script does is write that exact authored string back. If the
 * script never runs, fails, the visitor asked for less motion, or we are in the
 * theme editor, the figure simply sits at its final value — the same contract
 * about-timeline.js follows.
 *
 * The parsing is the load-bearing part. These are merchant-editable free-text
 * fields carrying Dutch formatting, not numbers:
 *
 *   1986    no separator          -> must NOT become "1.986"
 *   1.800+  "." groups thousands  -> must stay grouped, keep the "+"
 *   500+    plain + suffix
 *   9,2     "," is the decimal    -> must keep the comma and its one decimal
 *
 * Whether to group is decided by whether the AUTHORED value was grouped, which
 * is what keeps a year from growing a separator. Anything that does not parse as
 * a number is left completely alone — a merchant can type anything into that
 * field and a count-up must never mangle it.
 */

const COUNT_DURATION = 1200;

class AboutStats extends HTMLElement {
  connectedCallback() {
    this.targets = Array.from(this.querySelectorAll('[data-count-up]'))
      .map((el) => {
        const parsed = AboutStats.parse(el.textContent.trim());
        return parsed ? { el, ...parsed } : null;
      })
      .filter(Boolean);
    if (this.targets.length === 0) return;

    // The editor re-renders a section on every setting change; replaying a
    // count each time would fight the merchant's own edit.
    const inEditor = Boolean(window.Shopify && window.Shopify.designMode);
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (inEditor || reduced || typeof IntersectionObserver === 'undefined') return;

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.unobserve(this);
        this.run();
      },
      { rootMargin: '0px 0px -50px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  // "1.800+" -> { prefix: '', suffix: '+', value: 1800, decimals: 0, grouped: true, original: '1.800+' }
  // Returns null for anything that is not a number, which leaves the text untouched.
  static parse(original) {
    const match = original.match(/^(\D*?)([\d.,]+)(\D*)$/);
    if (!match) return null;
    const [, prefix, core, suffix] = match;
    const grouped = core.includes('.');
    const decimalPart = core.split(',')[1] || '';
    const value = Number.parseFloat(core.replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(value)) return null;
    return { prefix, suffix, value, decimals: decimalPart.length, grouped, original };
  }

  run() {
    for (const target of this.targets) {
      const format = new Intl.NumberFormat('nl-NL', {
        minimumFractionDigits: target.decimals,
        maximumFractionDigits: target.decimals,
        useGrouping: target.grouped,
      });
      let startedAt = null;
      const step = (now) => {
        if (startedAt === null) startedAt = now;
        const elapsed = now - startedAt;
        if (elapsed >= COUNT_DURATION) {
          // Land on the authored string itself, never on a re-formatted
          // approximation of it — the resting DOM must match the design exactly.
          target.el.textContent = target.original;
          return;
        }
        const progress = elapsed / COUNT_DURATION;
        const eased = 1 - Math.pow(1 - progress, 3);
        target.el.textContent = target.prefix + format.format(target.value * eased) + target.suffix;
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
  }
}

if (!customElements.get('about-stats')) {
  customElements.define('about-stats', AboutStats);
}
